import { isBefore } from 'date-fns'
import { t } from 'i18next'
import React from 'react'

import { normalizePrice, getMintPrice } from '@common/functions'
import { MintButton } from '@common/library'
import CheckerDate from '@components/CheckerDate'
import ProgressRounded from '@components/ProgressRounded'
import SelectQuantity from '@components/SelectQuantity'
import useMain from '@hooks/useMain'
import useNetwork from '@hooks/useNetwork'
import useNetworkWallet from '@hooks/useNetworkWallet'
import {
    DescriptionColSubTitle,
    DescriptionColText,
    DescriptionColTitle,
    DescriptionProgressAndCounter,
    DescriptionSectionAnchor,
    DescriptionSectionButtonRow,
    DescriptionSectionCol,
    DescriptionSectionColEnd,
    DescriptionSectionContainer,
    DescriptionSectionDivider,
    DescriptionSectionMintedCounter,
    DescriptionSectionPrice,
    DescriptionSectionRow,
    DescriptionSectionRowInfos,
    DescriptionSectionTitle,
} from '@styles/layouts/home/DescriptionSection/styles'

const DescriptionSection: React.FC = () => {
    const {
        state: {
            mainInit: { maxPerTransaction, customization },
            selectedSalesBehaviour: { mintPrice, startDate, dutchAuction, hasWhiteList },
            config,
        },
        operations: { setCurrentStep },
    } = useMain()

    const {
        state: {
            stage,
            remainedTokenCount,
            totalSupply,
            currentMintPrice,
            contractName,
            contractAddress,
            network,
            blockchain,
            selectedOption,
        },
        operations: { setSelectedOption, handleChange, setCurrentMintPrice },
    } = useNetwork()

    const {
        state: { isLoadingContext, suggestedGasFees, truncatedContractAddress },
        operations: { setEstimatedTotalCost },
    } = useNetworkWallet()

    const shouldDisable = React.useMemo(
        () =>
            isLoadingContext ||
            isBefore(
                new Date(),
                new Date(
                    startDate.year,
                    startDate.month - 1,
                    startDate.day,
                    startDate.hour,
                    startDate.minute,
                    startDate.second,
                ),
            ),
        [isLoadingContext, remainedTokenCount, stage, startDate, hasWhiteList],
    )

    const hasMintedCount = React.useMemo(() => {
        if (typeof customization?.interface?.hasMintedCount === 'boolean')
            return customization?.interface?.hasMintedCount

        return true
    }, [customization?.interface?.hasMintedCount])

    // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
    const handleCurrentMintPriceChange = () => {
        const price = getMintPrice(mintPrice, startDate, dutchAuction)
        setCurrentMintPrice(normalizePrice(price, 1))
    }

    const navigateToPayment = React.useCallback(() => {
        setCurrentStep('payments')
    }, [setCurrentStep])

    return (
        <DescriptionSectionContainer>
            <DescriptionSectionCol>
                <DescriptionSectionTitle id="description-contract-name">{contractName}</DescriptionSectionTitle>
                {hasMintedCount && (
                    <DescriptionProgressAndCounter>
                        <ProgressRounded limit={totalSupply} remainedTokenCount={remainedTokenCount} />
                        <DescriptionSectionMintedCounter>
                            {remainedTokenCount === -1 ? (
                                <>{t('home.loadingMessages.wait')}</>
                            ) : (
                                <span id="description-section-minted-counter">
                                    <span id="remainedTokenCount">{remainedTokenCount}</span>
                                    <span> out of </span>
                                    <span id="totalSupply">{totalSupply}</span> minted
                                </span>
                            )}
                        </DescriptionSectionMintedCounter>
                    </DescriptionProgressAndCounter>
                )}
            </DescriptionSectionCol>
            <DescriptionSectionDivider />
            <DescriptionSectionRowInfos>
                <DescriptionSectionCol>
                    <DescriptionColTitle>Network</DescriptionColTitle>
                    <DescriptionColText id="network">{network}</DescriptionColText>
                </DescriptionSectionCol>

                <DescriptionSectionCol>
                    <DescriptionColTitle>Blockchain</DescriptionColTitle>
                    <DescriptionColText id="blockchain">{config.networks[network][blockchain].name}</DescriptionColText>
                </DescriptionSectionCol>

                <DescriptionSectionCol>
                    <DescriptionColTitle>Contract address</DescriptionColTitle>
                    <DescriptionSectionAnchor
                        href={`${config.networks[network][blockchain].explorer_address}${contractAddress}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        id="contract-adress"
                    >
                        {truncatedContractAddress}
                    </DescriptionSectionAnchor>
                </DescriptionSectionCol>
            </DescriptionSectionRowInfos>
            <DescriptionSectionDivider />

            <DescriptionSectionRow>
                <DescriptionSectionCol>
                    <DescriptionColTitle>Quantity</DescriptionColTitle>
                    <SelectQuantity
                        mintPrice={currentMintPrice}
                        maxPerTransaction={maxPerTransaction}
                        selectedOption={selectedOption}
                        setSelectedOption={setSelectedOption}
                        suggestedGasFees={suggestedGasFees}
                        setEstimatedTotalCost={setEstimatedTotalCost}
                        handleChange={handleChange}
                        id="description-unit-quantity"
                    />
                    <DescriptionColSubTitle>
                        Max: <span id="max-per-transaction">{maxPerTransaction}</span> per mint
                    </DescriptionColSubTitle>
                </DescriptionSectionCol>

                <DescriptionSectionColEnd>
                    <DescriptionSectionPrice>Price</DescriptionSectionPrice>
                    <DescriptionColTitle isPrice>
                        <span id="description-unit-price">{`${currentMintPrice}`} </span>
                        <span id="description-unit-network"> {`${config.networks[network][blockchain].currency}`}</span>
                    </DescriptionColTitle>
                    {/* <DescriptionColSubTitle>$ 3,090.00</DescriptionColSubTitle> */}
                </DescriptionSectionColEnd>
            </DescriptionSectionRow>
            <DescriptionSectionDivider />

            <DescriptionSectionButtonRow>
                <MintButton disabled={shouldDisable} onClick={navigateToPayment}>
                    {remainedTokenCount === -1 ? t('home.soldOut') : t('home.buyNow')}
                </MintButton>
                <CheckerDate
                    id="timer-description-section"
                    startDate={startDate}
                    dutchAuction={dutchAuction}
                    handleCurrentMintPriceChange={handleCurrentMintPriceChange}
                />
            </DescriptionSectionButtonRow>
        </DescriptionSectionContainer>
    )
}

export default DescriptionSection
